// To Use:
// import { setSeRem } from 'se-fe-themes'
// setSeRem()
function setSeRem() {
    const html = document.documentElement;
    if (html.style.getPropertyValue('--se-rem'))
        return;
    const px = parseFloat(getComputedStyle(html).getPropertyValue('font-size'));
    if (isNaN(px)) {
        setTimeout(setSeRem, 50);
    }
    else {
        const seRem = Math.round(16 / px * 10000) / 10000;
        html.style.setProperty('--se-rem', `${seRem}rem`);
    }
}

const STAGING = /(ngin-staging\.com|sestage\.us)$/i;
const DEV = /(^localhost|\.dev$|\.test$)/i;
const PROD_URL = 'https://se-fe-angular-services.ui.sportngin.com';
const STAGING_URL = 'https://se-fe-angular-services.ui.stage.ngin-staging.com';
const DEV_URL = 'http://localhost:8008';
function seFeThemeUrl(host = window.location.hostname) {
    if (DEV.exec(host))
        return DEV_URL;
    if (STAGING.exec(host))
        return STAGING_URL;
    return PROD_URL;
}

var SeFeThemes;
(function (SeFeThemes) {
    SeFeThemes["Light"] = "light";
    SeFeThemes["Dark"] = "dark";
    SeFeThemes["Device"] = "device";
})(SeFeThemes || (SeFeThemes = {}));
const THEME_SEPARATOR = '|';
const THEME_KEY = 'se-fe-theme';
const SUPPORTED_THEMES = Object.values(SeFeThemes).join(THEME_SEPARATOR);
const SUPPORTED_THEMES_KEY = 'se-fe-themes';
const DEFAULT_THEME = SeFeThemes.Light;

class SeFeTheme {
    constructor(options = {}) {
        this.id = `${SeFeTheme.counter++}-${Date.now()}`;
        this.onMessage = (message) => {
            const msg = message?.data || {};
            if (msg.type !== SeFeTheme.themeKey)
                return;
            const { userTheme, appTheme, destroy } = msg.data;
            if (destroy === this.id)
                return this.destroy();
            if (message.source !== this.apiFrame.contentWindow)
                return;
            if (appTheme !== this.currentTheme) {
                this.settings = { userTheme, appTheme };
                localStorage.setItem(SeFeTheme.themeKey, appTheme); // cahce theme for quick application next time
                this.applyTheme(appTheme, true);
            }
        };
        if (!options.url)
            options.url = seFeThemeUrl();
        Object.assign(this, options);
        this.applyStyles();
        this.applyTheme(SeFeTheme.tempTheme || SeFeTheme.defaultTheme, false);
        this.loadFonts();
        this.loadApi();
    }
    static init(options = {}) {
        setSeRem(); // sets font size
        const { onChange, shadowRoot } = options; // always apply theme new options
        if (this.options)
            console.warn('SeFeTheme already initialized with options', this.options);
        this.options || (this.options = { ...options });
        return new SeFeTheme({ ...this.options, onChange, shadowRoot });
    }
    get onChange() {
        return this._onChange;
    }
    set onChange(fn) {
        this._onChange = fn;
        this.applyChange();
    }
    get win() { return window; }
    get root() { return this.shadowRoot?.host || document.documentElement; }
    get styleRoot() { return this.shadowRoot || document.head; }
    get frameRoot() { return this.shadowRoot || document.body; }
    get apiThemes() { return SeFeTheme.overrideThemes || this.supportedThemes; }
    setTheme(theme) {
        this.apiFrame.contentWindow.postMessage({ type: SeFeTheme.themeKey, data: theme }, '*');
    }
    destroy() {
        if (this.frameRoot.parentNode || this.frameRoot.host?.parentElement)
            return;
        this.win.removeEventListener('message', this.onMessage);
        this.apiFrame.remove();
        // No need to remove style tag (it could be used later)
    }
    loadApi() {
        this.apiFrame = document.createElement('iframe');
        this.apiFrame.style.cssText = 'position:absolute;top:0;left:0;width:0;height:0;border:0;';
        this.apiFrame.setAttribute('inert', 'true');
        this.apiFrame.setAttribute('src', `${this.url}/se-fe-themes/index.html?id=${this.id}${this.apiThemes ? `&themes=${this.apiThemes}` : ''}`);
        this.win.addEventListener('message', this.onMessage);
        this.onReady(() => { this.frameRoot.append(this.apiFrame); });
    }
    applyChange() {
        const theme = this.currentTheme;
        const settings = this.settings || this.currentTheme ? { appTheme: theme, userTheme: theme } : undefined;
        if (settings && this.onChange)
            this.onChange(settings);
    }
    applyStyles() {
        const styles = this.win.seFeThemeStyleTag?.cloneNode(true);
        if (styles)
            this.styleRoot.prepend(styles);
    }
    applyTheme(theme, change) {
        if (!theme)
            return;
        const { classList } = this.root;
        const newClass = `${SeFeTheme.themeKey}--${theme}`;
        const prevTheme = this.currentTheme;
        this.currentTheme = theme;
        if (prevTheme === theme)
            return;
        if (prevTheme) {
            classList.add(`${SeFeTheme.themeKey}--switching`);
            classList.replace(`${SeFeTheme.themeKey}--${prevTheme}`, newClass);
            setTimeout(() => classList.remove(`${SeFeTheme.themeKey}--switching`), 1000);
        }
        else {
            classList.add(newClass);
            classList.add(`${SeFeTheme.themeKey}--loaded`);
        }
        if (change)
            this.applyChange();
    }
    loadFontFace(family, weight, style, url) {
        const ckey = `${family}-${weight}-${style}`;
        if (SeFeTheme.fontCache.has(ckey))
            return;
        SeFeTheme.fontCache.add(ckey);
        const font = new this.win.FontFace(family, `url('${this.url}/se-fe-themes/assets/${url}')`, { style, weight });
        this.win.document.fonts.add(font);
        font.load().catch(() => { });
    }
    loadFonts() {
        this.loadFontFace('figtree', '1 999', 'normal', 'figtree-variable-weight.woff2');
        this.loadFontFace('figtree', '1 999', 'italic', 'figtree-italic-variable-weight.woff2');
    }
    onReady(fn) {
        /^(interactive|complete)$/.exec(document.readyState) ? fn() : document.addEventListener('DOMContentLoaded', fn);
    }
}
SeFeTheme.counter = 1;
SeFeTheme.options = undefined;
SeFeTheme.themeKey = THEME_KEY;
SeFeTheme.themeSeparator = THEME_SEPARATOR;
SeFeTheme.supportedThemes = SUPPORTED_THEMES;
SeFeTheme.supportedThemesKey = SUPPORTED_THEMES_KEY;
SeFeTheme.lightTheme = SeFeThemes.Light;
SeFeTheme.defaultTheme = DEFAULT_THEME; // will load if none set
SeFeTheme.tempTheme = undefined; // will load if preset for the local domain
SeFeTheme.darkTheme = SeFeThemes.Dark;
SeFeTheme.deviceTheme = SeFeThemes.Device;
SeFeTheme.fontCache = new Set();
// NOW: set manually in local storage to averride app supportedThemes
// LATER: use user pref in se bar to push override themes into apiFrame domain, then push back to loacl domains
SeFeTheme.overrideThemes = undefined;

// Support existing method until SeFeDocsPage can be updated
function setSeTheme(_theme, shadowRoot) {
    SeFeTheme.init({ shadowRoot });
}

// Support existing method until SeFeDocsPage can be updated
function loadSeFeTheme(options = {}, win = window) {
    const promise = new Promise(resolve => {
        const parent = options.shadowRoot || win.document.head;
        const url = options.url || seFeThemeUrl();
        const loader = win.document.createElement('script');
        loader.src = `${url}/se-fe-themes/loader.js?skipInit`;
        loader.onload = () => {
            win.SeFeTheme.init(options);
            resolve();
        };
        loader.onError = () => {
            resolve();
        };
        parent.append(loader);
    });
    return promise;
}
class SeFeThemesLoader {
}
// @dynamic
SeFeThemesLoader.load = loadSeFeTheme;

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_THEME, SUPPORTED_THEMES, SUPPORTED_THEMES_KEY, SeFeTheme, SeFeThemes, SeFeThemesLoader, THEME_KEY, THEME_SEPARATOR, loadSeFeTheme, seFeThemeUrl, setSeRem, setSeTheme };
